import React from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'motion/react';

import { useToggle } from '../../../hooks';
import { EventType } from '../../../interfaces';
import { Dot } from '../../atoms/dot';
import { FadeIn } from '../../atoms/fadeIn';
import { Relative } from '../../atoms/relative';
import { Text } from '../../atoms/text';
import { ToggleSwitch } from '../../atoms/toggleSwitch';
import { WithClickOutside } from '../../atoms/withClickOutside';
import { Pill } from './Pill';

interface Props {
  hiddenEventTypes: EventType[];
  isSelected: (id: number) => boolean;
  onToggle: (eventType: EventType) => void;
  renderTrigger?: (triggerProps: {
    onClick: () => void;
    'aria-expanded': boolean;
    'aria-label'?: string;
  }) => React.ReactNode;
  menuStyles?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  showDots?: boolean;
  header?: string;
  children?: React.ReactNode;
  selectAll?: { label: string; active: boolean; onToggle: () => void };
}

export const EventTypeDropdown = (props: Props) => {
  const {
    hiddenEventTypes,
    isSelected,
    onToggle,
    renderTrigger,
    showDots = true,
    header = 'Event Type',
  } = props;
  const [isOpen, toggleOpen, setIsOpen] = useToggle(false);

  const triggerProps = {
    onClick: toggleOpen,
    'aria-expanded': isOpen,
    ...(renderTrigger ? {} : { 'aria-label': 'Show more event types' as const }),
  };

  return (
    <WithClickOutside
      onClickOutside={() => setIsOpen(false)}
      style={props.wrapperStyle ?? { display: 'inline-flex' }}
    >
      <Relative>
        {renderTrigger ? (
          renderTrigger(triggerProps)
        ) : (
          <Pill short {...triggerProps}>
            +
          </Pill>
        )}

        <AnimatePresence mode="popLayout">
          {isOpen && (
            <FadeIn key="dropdown" duration={0.1} style={{ zIndex: 999 }}>
              <DropdownPanel role="menu" style={props.menuStyles}>
                {header && (
                  <SmallerText variant="secondary" style={{ marginLeft: '12px' }}>
                    {header}
                  </SmallerText>
                )}

                {props.children}

                {props.selectAll && (
                  <SelectAllRow role="menuitem" onClick={props.selectAll.onToggle}>
                    <SmallerText variant="primary">
                      {props.selectAll.label}
                    </SmallerText>
                    <ToggleSwitch active={props.selectAll.active} />
                  </SelectAllRow>
                )}

                {hiddenEventTypes.map((et) => (
                  <Row key={et.id} role="menuitem" onClick={() => onToggle(et)}>
                    <SmallerText variant="primary">
                      {showDots && (
                        <Dot color={et.marker_color_hex} size={8} style={{ marginTop: 0 }} />
                      )}
                      {et.name}
                    </SmallerText>

                    <ToggleSwitch active={isSelected(et.id)} />
                  </Row>
                ))}
              </DropdownPanel>
            </FadeIn>
          )}
        </AnimatePresence>
      </Relative>
    </WithClickOutside>
  );
};

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 999;

  border-radius: 6px;
  border: 1px solid var(--border-secondary, #e0e1e9);
  background: var(--bg-primary-neutral, #fff);

  /* box-shadow/popper */
  box-shadow: 0 1px 12px 0 rgba(25, 27, 35, 0.15);

  padding: 12px 0 4px 0;
  gap: 0;
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
`;

const SmallerText = styled(Text)`
  font-size: 14px;
  white-space: nowrap;
  margin-bottom: 0;
`;

const SelectAllRow = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  transition: 150ms linear background;
  padding: 4px 12px;
  border: none;
  border-bottom: 1px solid var(--border-secondary, #e0e1e9);
  margin-bottom: 4px;
  cursor: pointer;
  text-align: left;
  background-color: #FAFAFA;

  &:hover {
    background: #f5f5f7;
  }
`;

const Row = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  transition: 150ms linear background;
  padding: 4px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;

  ${Dot} {
    margin-left: 0;
  }

  &:hover {
    background: #f5f5f7;
  }
`;
